import { useState } from "react";
import { admin } from "../../api/client";
import type { KybCase } from "../../api/types";
import { can, staffRole, useMe } from "../../auth/session";
import { TocaPage } from "../../components/Shell";
import { Empty, ErrorBox, Pill, Spinner, useToast } from "../../components/ui";
import { useAsync } from "../../hooks/useAsync";
import { fmt, t } from "../../i18n";
import { dateTimeShort, formatCNPJ } from "../../lib/format";
import { DecisionModal, KybStatusPill } from "./shared";

const STATUSES = ["in_review", "submitted", "needs_more_info", "approved", "rejected"];

export function TocaKyb() {
  const me = useMe();
  const toast = useToast();
  const [status, setStatus] = useState("in_review");
  const { data, error, loading, reload } = useAsync(() => admin.kybCases(status), [status]);
  const [deciding, setDeciding] = useState<KybCase | null>(null);
  const allowed = can.decideKyb(staffRole(me));

  return (
    <TocaPage title={t.toca.kybTitle}>
      <div className="chips" role="tablist">
        {STATUSES.map((s) => (
          <button key={s} type="button" className="chip" role="tab" aria-selected={status === s} onClick={() => setStatus(s)}>
            {t.toca.kybStatus[s]}
          </button>
        ))}
      </div>
      <div className="card">
        {loading && !data ? (
          <Spinner />
        ) : error ? (
          <div style={{ padding: 16 }}><ErrorBox error={error} onRetry={reload} /></div>
        ) : !data || data.length === 0 ? (
          <Empty body={t.toca.kybEmpty} />
        ) : (
          <div className="tscroll" tabIndex={0}>
            <table>
              <thead>
                <tr>
                  <th>{t.toca.kybColCompany}</th>
                  <th>{t.common.status}</th>
                  <th>{t.toca.kybColFlags}</th>
                  <th>{t.toca.kybColSubmitted}</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {data.map((c) => (
                  <tr key={c.id}>
                    <td>
                      <div className="cell2">
                        <b>{c.company?.legalName ?? c.companyId ?? c.id}</b>
                        <span className="mono">{c.company?.cnpj ? formatCNPJ(c.company.cnpj) : c.id}</span>
                      </div>
                    </td>
                    <td><KybStatusPill status={c.status} /></td>
                    <td>
                      <div className="row" style={{ gap: 6 }}>
                        {(c.flags ?? []).map((f) => <Pill key={f} tone="warn">{f}</Pill>)}
                        {(c.reasons ?? []).length > 0 && <span className="muted small">{(c.reasons ?? []).join(" · ")}</span>}
                        {!(c.flags ?? []).length && !(c.reasons ?? []).length && <span className="muted">—</span>}
                      </div>
                    </td>
                    <td className="muted" style={{ whiteSpace: "nowrap" }}>{dateTimeShort(c.submittedAt)}</td>
                    <td className="r">
                      {allowed && (c.status === "in_review" || c.status === "submitted" || c.status === "needs_more_info") ? (
                        <button type="button" className="btn dark xs" onClick={() => setDeciding(c)}>{t.toca.kybDecide}</button>
                      ) : c.decisionNote ? (
                        <span className="muted small">{c.decisionNote}</span>
                      ) : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      {!allowed && <span className="muted small">{t.common.readonlyHint}</span>}
      {deciding && (
        <DecisionModal
          title={t.toca.kybDecide}
          subject={`${deciding.company?.legalName ?? deciding.id}${deciding.company?.cnpj ? " · CNPJ " + formatCNPJ(deciding.company.cnpj) : ""}`}
          options={[
            { value: "approved" as const, label: t.toca.kybDecision.approved! },
            { value: "needs_more_info" as const, label: t.toca.kybDecision.needs_more_info! },
            { value: "rejected" as const, label: t.toca.kybDecision.rejected!, danger: true },
          ]}
          onDecide={async (d, note) => {
            await admin.kybDecide(deciding.id, d, note);
            toast(fmt(t.toca.kybDecided, { d: t.toca.kybDecision[d] }));
            void reload();
          }}
          onClose={() => setDeciding(null)}
        />
      )}
    </TocaPage>
  );
}
